import { FirebaseStorageClient } from '../utils/firebase/storageClient';
import firebase from "firebase/compat/app";

export type AssetType = 'logo' | 'banner' | 'icon' | 'gallery' | 'other';

export interface UploadAssetOptions {
  charityId: string;
  assetType: AssetType;
  file: File;
  assetName?: string;
  metadata?: Record<string, any>;
}

export class CharityAssetsService {
  private storageClient: FirebaseStorageClient;

  constructor(firebaseInstance: typeof firebase) {
    this.storageClient = new FirebaseStorageClient(firebaseInstance);
  }
  private generateAssetPath(
    charityId: string,
    assetType: AssetType,
    fileName: string
  ): string {
    return `${charityId}/assets/${assetType}/${fileName}`;
  }

  private generateFileName(file: File, assetName?: string): string {
    const timestamp = Date.now();
    const extension = file.name.split('.').pop();
    const baseName = assetName || file.name.split('.')[0];
    return `${baseName}_${timestamp}.${extension}`;
  }

  private validateFile(file: File, assetType: AssetType): void {
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_TYPES = {
      logo: ['image/png', 'image/jpeg', 'image/svg+xml'],
      banner: ['image/png', 'image/jpeg', 'image/webp'],
      icon: ['image/png', 'image/jpeg', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'],
      gallery: ['image/png', 'image/jpeg', 'image/webp'],
      other: ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp']
    };

    if (file.size > MAX_SIZE) {
      throw new Error(`File size exceeds ${MAX_SIZE / 1024 / 1024}MB limit`);
    }

    const allowedTypes = ALLOWED_TYPES[assetType];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const isAllowed =
      allowedTypes.includes(file.type) ||
      (assetType === 'icon' && extension === 'ico');

    if (!isAllowed) {
      throw new Error(`File type ${file.type || extension} not allowed for ${assetType}`);
    }
  }

  async uploadAsset(options: UploadAssetOptions): Promise<string> {
    const { charityId, assetType, file, assetName } = options;

    this.validateFile(file, assetType);

    const fileName = this.generateFileName(file, assetName);
    const path = this.generateAssetPath(charityId, assetType, fileName);
    const downloadURL = await this.storageClient.uploadFile(file, path);

    return downloadURL;
  }

  async uploadLogo(
    file: File,
    charityId: string,
    logoName: string = 'navbar'
  ): Promise<string> {
    return this.uploadAsset({
      charityId,
      assetType: 'logo',
      file,
      assetName: logoName
    });
  }

  async uploadIcon(
    file: File,
    charityId: string,
    iconName: string = 'favicon'
  ): Promise<string> {
    return this.uploadAsset({
      charityId,
      assetType: 'icon',
      file,
      assetName: iconName
    });
  }

  async uploadBanner(
    file: File,
    charityId: string,
    bannerName: string = 'main'
  ): Promise<string> {
    return this.uploadAsset({
      charityId,
      assetType: 'banner',
      file,
      assetName: bannerName
    });
  }
}
